import "./styles.css";
import { Layout, Spin, Button } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useUserInfo } from "GlobalStates/User";
import { ReadinessScore } from "./ReadinessScore/ReadinessScore";
import { ROUTES } from "Features/Routes/routeConstants";
import { useEffect } from "react";
import { toast } from "react-toastify";
import CollegeRecommendations from "Features/Colleges/CollegeRecommendations";
import { UserWelcomeTitle } from "Components/UserWelcomeTitleCard";
import { EmptyCollegeRecommendations } from "./EmptyRecommendations/emptyRecommendations";
import { useQuery } from "@tanstack/react-query";
import { getCollegeRecommendations } from "./apis/recommendations";
import { FavouritesProvider } from "Features/Colleges/context/CollegeContext";
import { PremiumBanner } from "./PremiumBanner";
import { Badge } from "Components/Badge";
import { Crown, ArrowRight } from "lucide-react";
import { DeadlineCountdown } from "./GlobalClockAndDeadlines";
import { NextSteps } from "./NextSteps";
import { announcements, steps } from "./constants";
import { WeeklyHighlights } from "./WeeklyHighlights";
import { DemoVideo } from "./DemoVideo";
import { ApplicationTimeline } from "./ApplicationTimeline";
import { ProfileCompletion } from "./ProfileCard";
import { trackFunnelEvent } from "Config/analytics";

const { Content } = Layout;

const UserDashboard = () => {
  const { user } = useUserInfo();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: [user?.id, "colleges"],
    queryFn: () => getCollegeRecommendations(user?.id),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!user?.id) return;

    trackFunnelEvent("dashboard_view", {
      completed_college_finder: Boolean(user?.completedCollegeFinder),
      is_premium_user: Boolean(user?.isPremiumUser),
      tier: user?.tier,
    });
  }, [user?.id, user?.completedCollegeFinder, user?.isPremiumUser, user?.tier]);

  useEffect(() => {
    if (
      (state?.from === ROUTES.STRIPE_PAYMENT_PROCESSING ||
        state?.from === ROUTES.PHONEPE_PAYMENT_PROCESSING) &&
      state?.paymentSuccessStatus
    ) {
      toast.success("Your payment was successful!", {
        toastId: "payment-success",
      });
      navigate(ROUTES.DASHBOARD, { replace: true, state: {} });
    }
  }, [state, navigate]);

  useEffect(() => {
    if (searchParams?.get("next")) {
      navigate(searchParams?.get("next"), { replace: true, state: {} });
    }
  }, [navigate, searchParams]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard-container bg-whitesmoke w-full h-full">
      <div className="w-full">
        <UserWelcomeTitle />
        <Content className="content-container h-full w-full flex justify-center items-center">
          <div className="w-full flex flex-col justify-center items-center flex-1">
            <div className="flex wrap items-center m-4">
              <Badge
                variant={user?.isPremiumUser ? "success" : "warning"}
                size="lg"
                className="mr-4"
              >
                <Crown className="w-4 h-4 mr-1" />
                {user?.tier.charAt(0) +
                  user?.tier.slice(1).toLowerCase() +
                  " Plan"}
              </Badge>

              {!user?.isPremiumUser && (
                <Button
                  type="primary"
                  className="font-medium text-base rounded-lg h-fit hover:!text-black hover:scale-105 hover:!bg-[#80e280] hover:shadow-lg"
                  onClick={() => {
                    navigate(ROUTES.PRICING);
                  }}
                >
                  <div className="p-2 flex items-center">
                    Upgrade Now
                    <ArrowRight className="w-5 h-5 ml-2 inline" />
                  </div>
                </Button>
              )}
            </div>
            {!user?.isPremiumUser && <PremiumBanner />}
            <ReadinessScore />
          </div>
          <div className="flex w-full justify-center items-center flex-col flex-1">
            {isLoading && (
              <div className="flex items-center justify-center h-full">
                <Spin size="large" />
              </div>
            )}
            {isError && (
              <div className="flex items-center justify-center h-full">
                <h1 className="text-xl md:text-2xl">
                  There was an error fetching your recommendations, please check
                  later
                </h1>
              </div>
            )}
            {!isLoading &&
              !isError &&
              (user?.completedCollegeFinder ? (
                <FavouritesProvider>
                  <CollegeRecommendations
                    colleges={data}
                    isPremiumUser={user?.isPremiumUser}
                    showViewDetails={true}
                  />
                </FavouritesProvider>
              ) : (
                <EmptyCollegeRecommendations />
              ))}
          </div>
          <div className="flex justify-center items-center flex-col flex-1 w-[95%]">
            <DeadlineCountdown
              userTier={user?.tier}
              deadlines={[
                {
                  id: "1",
                  name: "Complete Your Profile",
                  date: "Today",
                  daysLeft: 0,
                  type: "regular",
                },
              ]}
            />
          </div>
          <div className="flex flex-wrap justify-around items-stretch gap-5 flex-1 w-[95%] mb-4">
            <div className="w-full flex-1 min-w-[300px]">
              <ProfileCompletion />
            </div>
            <div className="w-full flex-1 min-w-[300px]">
              <ApplicationTimeline isPremiumUser={user?.isPremiumUser} />
            </div>
          </div>
          <div className="flex flex-wrap justify-around items-stretch gap-5 flex-1 w-[95%] mb-4">
            <div className="w-full flex-1 min-w-[300px]">
              <NextSteps steps={steps} />
            </div>
            <div className="w-full flex-1 min-w-[300px]">
              <WeeklyHighlights announcements={announcements} />
            </div>
          </div>
          {!user?.isPremiumUser && (
            <div className="flex flex-wrap justify-around items-stretch gap-5 flex-1 w-[95%] mb-4">
              <div className="w-full">
                <DemoVideo />
              </div>
            </div>
          )}
        </Content>
      </div>
    </div>
  );
};

export default UserDashboard;
